/**
 * Scénarios de preview pour les professeurs : simuler progression, déblocages et XP (backend) d'un élève.
 * Utilisé en mode "Interface élève" pour tester différents états (débutant, intermédiaire, avancé).
 */
import { PARCOURS_NODE_ORDER } from '../data/parcoursTree'

const defaultNodeStat = (overrides = {}) => ({
  attempts: 0,
  successes: 0,
  consecutiveSuccesses: 0,
  averageScore: 0,
  masteryLevel: 'beginner',
  progressionMode: 'functions',
  unlocked: false,
  unlockedAt: null,
  lastAttemptAt: null,
  ...overrides
})

const masteredStat = () =>
  defaultNodeStat({
    attempts: 6,
    successes: 5,
    consecutiveSuccesses: 3,
    averageScore: 78,
    masteryLevel: 'intermediate',
    progressionMode: 'qcm',
    unlocked: true,
    unlockedAt: { toDate: () => new Date() },
    lastAttemptAt: { toDate: () => new Date() }
  })

const inProgressStat = (attempts = 2, consecutive = 1) =>
  defaultNodeStat({
    attempts,
    successes: 1,
    consecutiveSuccesses: consecutive,
    averageScore: 55,
    masteryLevel: 'beginner',
    progressionMode: 'functions',
    unlocked: true,
    unlockedAt: { toDate: () => new Date() },
    lastAttemptAt: { toDate: () => new Date() }
  })

function buildProgress(unlockedNodes, nodeStatsOverrides = {}) {
  const nodeStats = {}
  unlockedNodes.forEach((nodeId) => {
    nodeStats[nodeId] = nodeStatsOverrides[nodeId] ?? defaultNodeStat({
      unlocked: true,
      unlockedAt: { toDate: () => new Date() }
    })
  })
  return {
    userId: 'preview',
    unlockedNodes,
    nodeStats,
    updatedAt: { toDate: () => new Date() }
  }
}

/** Définition des scénarios : id, label, description, progression simulée, XP, streak, bestStreakEver (record) */
export const PREVIEW_SCENARIOS = [
  {
    id: 'beginner',
    label: 'Débutant',
    description: 'Seul le nœud 1.1 est débloqué, 0 tentative',
    classLabel: '6e - Orchestre debutant',
    xp: 0,
    streak: 0,
    bestStreakEver: 0,
    getProgress: () =>
      buildProgress(['1.1'], {
        '1.1': defaultNodeStat({ unlocked: true, unlockedAt: { toDate: () => new Date() } })
      })
  },
  {
    id: 'beginner_in_progress',
    label: '1.1 en cours',
    description: 'Quelques tentatives sur 1.1, pas encore maîtrisé',
    classLabel: '5e - Groupe A',
    xp: 25,
    streak: 1,
    bestStreakEver: 4,
    getProgress: () =>
      buildProgress(['1.1'], {
        '1.1': inProgressStat(3, 1)
      })
  },
  {
    id: 'stage1',
    label: 'Étape 1 maîtrisée',
    description: '1.1, cadences demi/parfaite, 1.2 débloqués',
    classLabel: '4e - Harmonie',
    xp: 180,
    streak: 3,
    bestStreakEver: 8,
    getProgress: () =>
      buildProgress(['1.1', 'cadence-demi', 'cadence-parfaite', '1.2'], {
        '1.1': masteredStat(),
        'cadence-demi': masteredStat(),
        'cadence-parfaite': masteredStat(),
        '1.2': defaultNodeStat({ unlocked: true, unlockedAt: { toDate: () => new Date() } })
      })
  },
  {
    id: 'stage2',
    label: 'Étape 2',
    description: 'Phase 2 (Densité dominante, Stabilité, Sixte-quarte, Cadence composée)',
    classLabel: '3e - Harmonie avancee',
    xp: 380,
    streak: 5,
    bestStreakEver: 12,
    getProgress: () => {
      const nodes = PARCOURS_NODE_ORDER.slice(0, PARCOURS_NODE_ORDER.indexOf('revision-etage-2'))
      const stats = {}
      nodes.forEach((id, i) => {
        const isLast = i === nodes.length - 1
        stats[id] = isLast
          ? defaultNodeStat({ unlocked: true, unlockedAt: { toDate: () => new Date() } })
          : masteredStat()
      })
      return buildProgress(nodes, stats)
    }
  },
  {
    id: 'intermediate',
    label: 'Intermédiaire',
    description: 'Jusqu\'à l\'étage 3 (cadence rompue, vi)',
    classLabel: '2nde - Analyse tonale',
    xp: 580,
    streak: 7,
    bestStreakEver: 21,
    getProgress: () => {
      const nodes = PARCOURS_NODE_ORDER.filter((id) =>
        ['4.1', '4.2', '4.3', '4.4', '4.5', '4.6'].indexOf(id) === -1
      )
      const stats = {}
      nodes.forEach((id, i) => {
        const isLast = i === nodes.length - 1
        stats[id] = isLast
          ? defaultNodeStat({ unlocked: true, unlockedAt: { toDate: () => new Date() } })
          : masteredStat()
      })
      return buildProgress(nodes, stats)
    }
  },
  {
    id: 'advanced',
    label: 'Avancé',
    description: 'Tout le parcours débloqué et maîtrisé',
    classLabel: '1ere - Preparation examen',
    xp: 850,
    streak: 14,
    bestStreakEver: 45,
    getProgress: () => {
      const nodes = [...PARCOURS_NODE_ORDER]
      const stats = {}
      nodes.forEach((id) => {
        stats[id] = masteredStat()
      })
      return buildProgress(nodes, stats)
    }
  }
]

export function getPreviewProgress(scenarioId) {
  const scenario = PREVIEW_SCENARIOS.find((s) => s.id === scenarioId)
  return scenario ? scenario.getProgress() : null
}

export function getPreviewXP(scenarioId) {
  const scenario = PREVIEW_SCENARIOS.find((s) => s.id === scenarioId)
  return scenario != null ? scenario.xp : null
}

export function getPreviewStreak(scenarioId) {
  const scenario = PREVIEW_SCENARIOS.find((s) => s.id === scenarioId)
  return scenario != null ? scenario.streak : null
}

export function getPreviewBestStreakEver(scenarioId) {
  const scenario = PREVIEW_SCENARIOS.find((s) => s.id === scenarioId)
  if (scenario == null) return null
  return scenario.bestStreakEver != null ? scenario.bestStreakEver : scenario.streak
}

export function getPreviewClassLabel(scenarioId) {
  const scenario = PREVIEW_SCENARIOS.find((s) => s.id === scenarioId)
  return scenario?.classLabel || null
}

/** Options de déblocage Horizons en mode preview */
export const PREVIEW_HORIZONS_OPTIONS = [
  { value: '', label: 'Aucun', shortLabel: 'Aucun' },
  { value: 'film', label: 'Film', shortLabel: 'Film' },
  { value: 'film,game', label: 'Film + JV', shortLabel: 'Film+JV' },
  { value: 'film,game,anime', label: 'Film + JV + Anime', shortLabel: 'Film+JV+Ani' },
  { value: 'film,game,anime,variety', label: 'Film + JV + Anime + Variété', shortLabel: '4 styles' },
  { value: 'film,game,anime,variety,pop', label: 'Tous', shortLabel: 'Tous' }
]

export function getPreviewScenarioDisplay(value) {
  if (!value) return { label: 'Réel (données prof)', shortLabel: 'Réel' }
  const scenario = PREVIEW_SCENARIOS.find((s) => s.id === value)
  if (!scenario) return { label: 'Réel (données prof)', shortLabel: 'Réel' }
  return { label: scenario.label, shortLabel: scenario.label }
}

export function getPreviewHorizonsDisplay(value) {
  const option = PREVIEW_HORIZONS_OPTIONS.find((o) => o.value === value)
  if (!option) return { label: 'Aucun', shortLabel: 'Aucun' }
  return { label: option.label, shortLabel: option.shortLabel }
}

export function getPreviewClassDisplay(value, classes = []) {
  if (!value) return { label: 'Aucune', shortLabel: 'Aucune' }
  const cls = classes.find((c) => c.id === value)
  const label = cls ? (cls.name || cls.code || cls.id) : value
  return { label, shortLabel: label }
}
